/**
 * Integration Activity — the "why did/didn't this work" log.
 *
 * One row per received email or voice call, each carrying three outcome chips:
 * Command (did it become a task), AMS (did it reach the activity log, email
 * only), and Insured (was a policyholder linked, email only). A green row needs
 * no explanation; a grey or red one expands to say exactly which gate stopped it
 * and what the configuration was at the time.
 *
 * The user has to recognise which item a row is, so each shows the sender/caller,
 * the subject or call title, and the opening words of the body/summary — never
 * the full email or transcript, which is neither stored nor needed here.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiRefreshCw,
  FiActivity,
  FiCheck,
  FiMinus,
  FiX,
  FiAlertTriangle,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';

import {
  listIntegrationActivity,
  ActivityItem,
  ActivitySource,
  STATUS_OK,
  STATUS_ERROR,
  LINK_LINKED,
  LINK_NOT_FOUND,
  LINK_NOT_ATTEMPTED,
} from '../../services/integrationActivityService';
const PAGE_SIZE = 25;

type ChipTone = 'ok' | 'skipped' | 'error' | 'muted';

interface ChipSpec {
  label: string;
  tone: ChipTone;
  title: string;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function toneIcon(tone: ChipTone) {
  if (tone === 'ok') return <FiCheck size={11} />;
  if (tone === 'error') return <FiAlertTriangle size={11} />;
  return <FiMinus size={11} />;
}

/** The Command outcome: created, or skipped/failed with the reason as the tooltip. */
function commandChip(item: ActivityItem): ChipSpec {
  if (item.command_status === STATUS_OK) {
    return {
      label: 'Command created',
      tone: 'ok',
      title: item.command_task_id ? `Task #${item.command_task_id}` : 'Command created',
    };
  }
  const why = item.command_reason_text || item.command_detail || 'No command was created.';
  return {
    label: item.command_status === STATUS_ERROR ? 'Command failed' : 'No command',
    tone: item.command_status === STATUS_ERROR ? 'error' : 'skipped',
    title: why,
  };
}

/** The AMS outcome. Null for voice, which has no AMS forward at all. */
function amsChip(item: ActivityItem): ChipSpec | null {
  if (item.ams_status === null || item.ams_status === undefined) {
    return null;
  }
  if (item.ams_status === STATUS_OK) {
    return { label: 'Stored in AMS', tone: 'ok', title: 'Written to the AMS activity log.' };
  }
  const why = item.ams_reason_text || item.ams_detail || 'Not stored in the AMS.';
  return {
    label: item.ams_status === STATUS_ERROR ? 'AMS failed' : 'Not in AMS',
    tone: item.ams_status === STATUS_ERROR ? 'error' : 'skipped',
    title: why,
  };
}

/** The insured link. Not attempted (e.g. voice) shows nothing rather than a false negative. */
function linkChip(item: ActivityItem): ChipSpec | null {
  if (item.link_status === null || item.link_status === undefined) return null;
  if (item.link_status === LINK_NOT_ATTEMPTED) return null;
  if (item.link_status === LINK_LINKED) {
    return { label: 'Insured linked', tone: 'ok', title: 'An insured was identified in the AMS.' };
  }
  if (item.link_status === LINK_NOT_FOUND) {
    return {
      label: 'No insured',
      tone: 'skipped',
      title: item.link_detail || 'No insured could be identified from this item.',
    };
  }
  return { label: 'Link failed', tone: 'error', title: item.link_detail || 'Insured lookup failed.' };
}

const Chip: React.FC<{ spec: ChipSpec }> = ({ spec }) => (
  <span className={`intg-activity__chip intg-activity__chip--${spec.tone}`} title={spec.title}>
    {toneIcon(spec.tone)}
    {spec.label}
  </span>
);

/** The expanded detail: every field that explains the decision, as its own line. */
const Detail: React.FC<{ item: ActivityItem; source: ActivitySource }> = ({ item, source }) => {
  const rows: Array<[string, string]> = [];

  if (item.command_detail) rows.push(['Command', item.command_detail]);
  if (item.command_reason_text) rows.push(['Why no command', item.command_reason_text]);
  if (item.ams_detail) rows.push(['AMS', item.ams_detail]);
  if (item.ams_reason_text) rows.push(['Why not in AMS', item.ams_reason_text]);
  if (item.ams_http_status) rows.push(['AMS response', String(item.ams_http_status)]);
  if (item.link_detail) rows.push(['Insured lookup', item.link_detail]);

  if (source === 'email') {
    rows.push(['Process label', item.process_label ? item.process_label : 'Not configured']);
    rows.push(['Labels on this item', item.labels ? item.labels : 'None']);
    if (item.event_type) rows.push(['Event type', item.event_type]);
  } else if (source === 'sms') {
    if (item.party_from) rows.push(['From', item.party_from]);
    if (item.party_to) rows.push(['To', item.party_to]);
  } else if (item.call_length_secs) {
    rows.push(['Call length', `${item.call_length_secs}s`]);
  }

  if (item.insured_id) rows.push(['Insured id', item.insured_id]);
  if (item.policy_id) rows.push(['Policy id', item.policy_id]);
  if (item.created_at) rows.push(['Received', new Date(item.created_at).toLocaleString()]);

  return (
    <dl className="intg-activity__detail">
      {rows.map(([label, value]) => (
        <div className="intg-activity__detail-row" key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
};

export interface IntegrationActivityModalProps {
  /** Which integration's activity to show — the modal is opened from that
   *  integration's own settings, so the source is fixed rather than switchable. */
  source: ActivitySource;
  /** Heading, e.g. "Momentum email exchange". */
  title: string;
  onClose: () => void;
}

const IntegrationActivityModal: React.FC<IntegrationActivityModalProps> = ({
  source,
  title,
  onClose,
}) => {
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Kept in both a ref and state: the ref is what `load` reads (always current),
  // the state is what renders the "Load older" button.
  const [cursor, setCursor] = useState<{ at: string | null; id: number | null }>({
    at: null,
    id: null,
  });
  const cursorRef = useRef<{ at: string | null; id: number | null }>({ at: null, id: null });
  const [retentionDays, setRetentionDays] = useState(0);

  const load = useCallback(
    async (append: boolean) => {
      setLoading(true);
      setError(null);

      try {
        // Read the cursor from the ref, not from state: `load` is memoized on
        // [source, onlyProblems], so a state read here would see the value
        // captured when the callback was created — always the initial null — and
        // every "Load older" would refetch page 1.
        const from = append ? cursorRef.current : { at: null, id: null };
        const page = await listIntegrationActivity({
          source,
          onlyProblems,
          limit: PAGE_SIZE,
          cursorCreatedAt: from.at,
          cursorId: from.id,
        });
        setItems((prev) => (append ? [...prev, ...page.items] : page.items));
        const next = { at: page.nextCursorCreatedAt, id: page.nextCursorId };
        cursorRef.current = next;
        setCursor(next);
        setRetentionDays(page.retentionDays);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load activity.');
      } finally {
        setLoading(false);
      }
    },
    [source, onlyProblems],
  );

  useEffect(() => {
    setItems([]);
    cursorRef.current = { at: null, id: null };
    setCursor({ at: null, id: null });
    setExpandedId(null);
    load(false);
  }, [source, onlyProblems, load]);

  const hasMore = Boolean(cursor.at && cursor.id);

  return (
    <motion.div
      className="composio-project-modal intg-activity__overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="composio-project-modal__panel composio-project-modal__panel--wide intg-activity"
        role="dialog"
        aria-modal="true"
        aria-labelledby="intg-activity-modal-title"
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="composio-project-modal__header">
          <FiActivity size={18} />
          <h3 id="intg-activity-modal-title">{title} — Activity</h3>
          <button
            type="button"
            className="composio-project-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={16} />
          </button>
        </div>

        <p className="composio-project-modal__desc">
          Everything received here recently, and what happened to it — whether a
          command was created, whether it reached the AMS, and if not, why.
        </p>

      <div className="intg-activity__controls">
        <label className="intg-activity__filter">
          <input
            type="checkbox"
            checked={onlyProblems}
            onChange={(e) => setOnlyProblems(e.target.checked)}
          />
          Only show problems
        </label>

        <button
          className="integrations-events__refresh"
          onClick={() => load(false)}
          disabled={loading}
          title="Refresh"
        >
          <FiRefreshCw size={13} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="intg-activity__error">
          <FiAlertTriangle size={14} />
          {error}
        </div>
      )}

      {!error && items.length === 0 && !loading && (
        <div className="integrations-events__empty">
          <p>No activity yet</p>
          <span>
            {onlyProblems
              ? 'Nothing has failed — every item was processed.'
              : source === 'email'
                ? 'Emails appear here as they arrive.'
                : source === 'sms'
                  ? 'Text messages appear here as they arrive.'
                  : 'Calls appear here as they complete.'}
          </span>
        </div>
      )}

      {/* The scrolling region: the modal header, filter and footer stay put. */}
      <div
        className="integrations-events__list intg-activity__list"
        style={{ flex: 1, minHeight: 0, maxHeight: 'none' }}
      >
        {items.map((item) => {
          const chips = [commandChip(item), amsChip(item), linkChip(item)].filter(
            (c): c is ChipSpec => c !== null,
          );
          const isExpanded = expandedId === item.id;
          return (
            <div className="integrations-events__item intg-activity__item" key={item.id}>
              <div className="integrations-events__item-icon">
                {source === 'email' ? '📧' : source === 'sms' ? '💬' : '📞'}
              </div>
              <div className="integrations-events__item-body">
                <div className="integrations-events__item-from">
                  {item.party_name || item.party_from || 'Unknown'}
                </div>
                <div className="integrations-events__item-subject">
                  {item.subject || 'No subject'}
                </div>
                {item.preview && (
                  <div className="integrations-events__item-snippet">{item.preview}</div>
                )}

                <div className="intg-activity__chips">
                  {chips.map((spec) => (
                    <Chip spec={spec} key={spec.label} />
                  ))}
                  <button
                    className="intg-activity__expand"
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                    {isExpanded ? 'Less' : 'Details'}
                  </button>
                </div>

                {isExpanded && <Detail item={item} source={source} />}
              </div>
              <div className="integrations-events__item-time">
                {item.created_at ? formatRelativeTime(item.created_at) : ''}
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && (
        <button className="intg-activity__more" onClick={() => load(true)} disabled={loading}>
          {loading ? 'Loading…' : 'Load older'}
        </button>
      )}

      {retentionDays > 0 && items.length > 0 && (
        <p className="intg-activity__retention">
          Activity is kept for {retentionDays} days.
        </p>
      )}

        <div className="composio-project-modal__actions" style={{ marginTop: 14 }}>
          <button
            type="button"
            className="integration-btn integration-btn--connect"
            onClick={onClose}
          >
            <FiCheck size={14} /> Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default IntegrationActivityModal;
